import HeaderAgentIcon from "@/assets/header-facilities-agent.svg";
import { IconlyExternalLink } from "@/core/assets/icons";
import { Badge } from "@/core/components/ui/badge";
import { BetaTag } from "@/core/components/floating-chatbot/components/beta-tag";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@/core/components/ui/sidebar";
import { ROUTES } from "@/core/routes/constants";
import { EVENTS, useActionsTrack } from "@/core/services/menu-analytics";
import { useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Fragment } from "react";
import { useWosMenu } from "../hooks/useWosMenu";

export function WosSidebarMenu() {
  const { state, toggleSidebar, isMobile } = useSidebar();
  const { items } = useWosMenu();
  const { onActionClick } = useActionsTrack();
  const [translate] = useTranslation(["core"]);

  const location = useLocation();

  const isCollapsed = state === "collapsed";

  const onTrackAndCollapse = (tracker?: string) => {
    if (tracker) {
      onActionClick(tracker as keyof typeof EVENTS, "MENU");
    }
    if (isMobile) {
      toggleSidebar();
    }
  };

  const isActionCenterActive = location.pathname.startsWith(
    ROUTES.ACTION_CENTER,
  );

  return (
    <SidebarContent className="px-2 py-2">
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActionCenterActive}
                tooltip={translate("app-menu-action-center") as string}
                data-testid="wos-menu-action-center"
                className="hover:bg-white data-[active=true]:bg-white"
              >
                <Link
                  to={ROUTES.ACTION_CENTER}
                  onClick={() => onTrackAndCollapse("MENU_ACTION_CENTER_CLICK")}
                  className={`group/item flex items-center gap-2 ${
                    isCollapsed ? "justify-center" : ""
                  }`}
                >
                  <img
                    src={HeaderAgentIcon}
                    alt=""
                    className="size-5 shrink-0"
                  />
                  {!isCollapsed && (
                    <>
                      <span
                        className={`flex-1 font-body text-sm leading-[150%] text-warmgray-700 group-hover/item:font-bold ${
                          isActionCenterActive ? "font-bold" : "font-medium"
                        }`}
                      >
                        {translate("app-menu-action-center")}
                      </span>
                      <BetaTag />
                    </>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>

      {items.map((section, index) => (
        <Fragment key={section.title}>
          {index > 0 && (
            <SidebarSeparator className="border-b border-[#DCDBDA] my-3" />
          )}
          <SidebarGroup className="py-0">
            <SidebarGroupLabel className="px-2 text-xs font-medium text-sidebar-foreground/70">
              {section.title}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {section.items.map((item) => {
                  const link = item.link?.split("/")[1];
                  const isActive =
                    !!link && location.pathname.startsWith(`/${link}`);
                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.title}
                        className="hover:bg-white data-[active=true]:bg-white"
                      >
                        {item.onSelect ? (
                          // Itens sem URL conhecida de antemão (ex.: SSO
                          // handoff, PED-13066): o próprio item resolve
                          // navegação e tracking, não há rota ativa. O ícone
                          // de link externo ao final sinaliza que o destino é
                          // outro sistema, aberto em nova aba (design Figma).
                          <button
                            type="button"
                            onClick={() => {
                              item.onSelect?.();
                              if (isMobile) toggleSidebar();
                            }}
                            className={`group/item flex w-full items-center gap-2 [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:text-darkgray-400 ${
                              isCollapsed ? "justify-center" : ""
                            }`}
                          >
                            {item.icon()}
                            {!isCollapsed && (
                              <>
                                <span className="flex-1 text-left font-body text-sm font-medium leading-[150%] text-warmgray-700 group-hover/item:font-bold">
                                  {item.title}
                                </span>
                                <IconlyExternalLink size={16} />
                              </>
                            )}
                          </button>
                        ) : item.external ? (
                          // Links externos: <a> nativo com target="_blank"
                          <a
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => onTrackAndCollapse(item.tracker)}
                            className={`group/item flex items-center gap-2 [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:text-darkgray-400 ${
                              isCollapsed ? "justify-center" : ""
                            }`}
                          >
                            {item.icon()}
                            {!isCollapsed && (
                              <>
                                <span
                                  className={`flex-1 font-body text-sm leading-[150%] text-warmgray-700 group-hover/item:font-bold ${
                                    isActive ? "font-bold" : "font-medium"
                                  }`}
                                >
                                  {item.title}
                                </span>
                                <IconlyExternalLink size={16} />
                              </>
                            )}
                          </a>
                        ) : (
                          // Links internos: <Link> do React Router (gera <a href> correto)
                          <Link
                            to={item.link}
                            onClick={() => onTrackAndCollapse(item.tracker)}
                            className={`group/item flex items-center gap-2 [&_svg]:size-5 [&_svg]:shrink-0 [&_svg]:text-darkgray-400 ${
                              isCollapsed ? "justify-center" : ""
                            }`}
                          >
                            {item.icon()}
                            {!isCollapsed && (
                              <span
                                className={`font-body text-sm leading-[150%] text-warmgray-700 group-hover/item:font-bold ${
                                  isActive ? "font-bold" : "font-medium"
                                }`}
                              >
                                {item.title}
                              </span>
                            )}
                            {item.badge && (
                              <Badge className="ml-0 bg-primary-500 text-primary hover:bg-primary-500/80">
                                {item.badge}
                              </Badge>
                            )}
                          </Link>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </Fragment>
      ))}
    </SidebarContent>
  );
}
